import { useCallback, useEffect, useState } from "react";
import { Info, Plus } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import { Button } from "../../primitives/button";
import {
  EntityCombobox,
  type EntityOption,
} from "../../patterns/EntityCombobox";
import { TODO_COLUMN_CLASS } from "./todoColumnLayout";
import { currentTime } from "@shared-core/domain/current-time";

// "Notes for today" on the sales home: good-to-know / FYI items a rep wants in
// front of them for the day. Deliberately NOT tickets — no status, owner,
// priority or completion, and nothing here is tracked. Anything that needs
// doing must be a ticket instead; the copy below says so on the page itself.
//
// A note must be attached to a customer (the backend requires an anchor), and
// shows that customer's name. It appears here on the day it is marked for and
// stays on the Notes page afterwards.

function localIsoDate(): string {
  const d = currentTime();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

export function TodayNotesColumn() {
  const dataAdapter = useDataAdapter();
  const [notes, setNotes] = useState<API.NoteForDayItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [customer, setCustomer] = useState<EntityOption | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await dataAdapter.getNotesForDay({
        day: localIsoDate(),
        pageSize: 50,
      });
      setNotes(result.items);
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [dataAdapter]);

  useEffect(() => {
    load();
  }, [load]);

  const fetchCustomers = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      const resp = await dataAdapter.getCustomersForIssuePicker({
        q: q || undefined,
        page: 1,
        pageSize: 25,
      });
      return resp.items.map((c) => ({ id: c.id, label: c.name }));
    },
    [dataAdapter],
  );

  const reset = () => {
    setAdding(false);
    setDraft("");
    setCustomer(null);
    setSaveFailed(false);
  };

  const canSave = draft.trim().length > 0 && customer !== null && !saving;

  const save = async () => {
    if (!canSave || !customer) return;
    setSaving(true);
    setSaveFailed(false);
    try {
      await dataAdapter.createNote({
        note_text: draft.trim(),
        customer_id: customer.id,
        remind_me_at: `${localIsoDate()}T00:00:00Z`,
        create_on_tharstern: false,
      });
      reset();
      await load();
    } catch {
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={TODO_COLUMN_CLASS}>
      <h3 className="text-base mb-1">
        Notes for today{" "}
        <span className="text-sm text-muted-foreground">({notes.length})</span>
      </h3>

      {/* Spans the description and filter rows the other columns use, so the
          lists below still line up. */}
      <div className="lg:row-span-2 mb-3">
        <p className="text-xs text-muted-foreground mb-2">
          Good-to-know reminders for today
        </p>
        <div
          role="note"
          className="flex gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
        >
          <Info className="size-3.5 shrink-0 mt-0.5" />
          <span>
            Notes are not tickets. They aren&apos;t tracked or assigned, and
            nothing here is an action. If something needs doing, create a ticket
            instead.
          </span>
        </div>
      </div>

      <div className="mb-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAdding(true)}
          disabled={adding}
        >
          <Plus className="size-3.5" />
          Add a note
        </Button>
      </div>

      <div>
        {adding && (
          <div className="mb-3 space-y-2">
            <EntityCombobox
              value={customer}
              onChange={setCustomer}
              fetchOptions={fetchCustomers}
              placeholder="Which customer is this about?"
              ariaLabel="Customer this note is about"
              className="w-full"
            />
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label="Note"
              placeholder="Something worth knowing today..."
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {saveFailed && (
              <p role="alert" className="text-xs text-destructive">
                Couldn&apos;t add the note. Please try again.
              </p>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={!canSave}>
                {saving ? "Adding…" : "Add note"}
              </Button>
              <Button size="sm" variant="ghost" onClick={reset}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-muted-foreground text-sm p-6 text-center border border-border rounded-lg">
            Loading...
          </div>
        ) : notes.length === 0 ? (
          <div className="text-muted-foreground text-sm p-6 text-center border border-border rounded-lg">
            No notes for today
          </div>
        ) : (
          <ul className="space-y-2">
            {notes.map((note) => (
              <li
                key={note.id}
                className="rounded-lg border border-border bg-card px-4 py-2.5 text-sm"
              >
                {note.customer_name && (
                  <div className="text-xs font-medium text-muted-foreground mb-0.5">
                    {note.customer_name}
                  </div>
                )}
                {note.note_text}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
